import React, { useEffect, useState } from "react";
import { Button, List, Modal } from "antd";
import { toast } from "react-toastify";
import { customLogger } from "../../../utils/Logger";
import ENDPOINTS from "../../../services/endpoint";
import apiInstance from "../../../services/apiService";
import ChatSidebarHeader from "../../../components/common/ChatSidebarHeader";
import CustomLoader from "../../../components/common/UI/Loader";
import ContactCard from "../../../components/Chat/ContactCard";
import GradientButton from "../../../components/common/UI/GradientButton";
import SearchInput from "../../../components/common/UI/SearchInput";
import CreateGroup from "../CreateGroup";
import "./index.scss";

interface NewGroupProps {
  handleBackClick: () => void;
}

interface Contact {
  _id: string;
  fullName: string;
  phoneNumber: string;
  emailAddress: string;
  iseFone: boolean;
  block?: any;
}

const NewGroup: React.FC<NewGroupProps> = ({ handleBackClick }) => {
  const [contactList, setContactList] = useState<Contact[] | null>(null);
  const [currentScreen, setCurrentScreen] = useState("add-members");
  const [filteredContacts, setFilteredContacts] = useState<Contact[] | null>(
    null
  );
  const [loading, setLoading] = useState(true);
  const [selectedContacts, setSelectedContacts] = useState<Contact[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [showBlockModal, setShowBlockModal] = useState<boolean>(false);
  const [selectedNumberToUnblock, setSelectedNumberToUnblock] =
    useState<Contact | null>(null);

  const handleContactSelection = (contact: Contact, checked: boolean) => {
    if (contact.block) {
      setSelectedNumberToUnblock(contact);
      setShowBlockModal(true);
      return;
    }

    setSelectedContacts((prev: Contact[]) => {
      const exists = prev.some((c) => c.phoneNumber === contact.phoneNumber);
      if (checked && !exists) {
        return [...prev, contact];
      } else if (!checked && exists) {
        return prev.filter((c) => c.phoneNumber !== contact.phoneNumber);
      }
      return prev;
    });
  };

  const handleUnblockContact = async () => {
    setLoading(true);
    try {
      const response = await apiInstance.delete(
        `${ENDPOINTS.BLOCK_NUMBER}/${selectedNumberToUnblock?.block?._id}`
      );

      if (response.status === 200) {
        toast.success("Phone Number updated successfully!");
        setShowBlockModal(false);

        const updatedNumber: Contact = {
          _id: selectedNumberToUnblock!._id,
          fullName: selectedNumberToUnblock!.fullName,
          phoneNumber: selectedNumberToUnblock!.phoneNumber,
          emailAddress: selectedNumberToUnblock!.emailAddress,
          iseFone: selectedNumberToUnblock!.iseFone,
        };

        handleContactSelection(updatedNumber, true);
      }
    } catch (error) {
      customLogger(error);
      toast.error("There was an error updating");
    } finally {
      setLoading(false);
    }
  };

  const handleNextStep = () => {
    if (!selectedContacts.length) {
      toast.error("Please select at least 1 member");
      return;
    } else {
      setCurrentScreen("create-group");
    }
  };

  useEffect(() => {
    const getContactsList = async () => {
      const URL = ENDPOINTS.CONTACTS_LIST;

      try {
        const response = await apiInstance.get(URL);
        const contacts = response.data.data;
        setContactList(contacts);
      } catch (err: any) {
        toast.error(err.msg || "Failed to get contacts");
      } finally {
        setLoading(false);
      }
    };

    getContactsList();
  }, [setContactList]);

  useEffect(() => {
    if (contactList) {
      setLoading(false);
      setFilteredContacts(contactList);
    }
  }, [contactList]);

  useEffect(() => {
    if (!contactList) return;

    const query = searchQuery?.toLowerCase();
    const filtered = contactList.filter(
      (contact: Contact) =>
        contact.fullName?.toLowerCase().includes(query) ||
        contact.phoneNumber.includes(query)
    );
    setFilteredContacts(filtered);
  }, [searchQuery, contactList]);

  return (
    <>
      {currentScreen === "add-members" && (
        <div className="select-members">
          <ChatSidebarHeader
            title="Add Members"
            handleBackClick={handleBackClick}
          />

          <div className="hero-section">
            <div className="search-input-container">
              <SearchInput
                value={searchQuery}
                onChange={setSearchQuery}
                placeholder="Search by name or phone number..."
              />
            </div>

            <div className="efone-contacts-list">
              <List
                dataSource={filteredContacts || []}
                loading={{
                  spinning: loading,
                  indicator: <CustomLoader />,
                }}
                renderItem={(contact: Contact) => {
                  if (!contact.iseFone) return;

                  const isChecked = selectedContacts.some(
                    (selected) => selected.phoneNumber === contact.phoneNumber
                  );

                  return (
                    <ContactCard
                      key={contact._id}
                      userName={contact.fullName}
                      phoneNumber={contact.phoneNumber}
                      isEfone={contact.iseFone}
                      checkbox
                      checked={isChecked}
                      onChecked={(checked) =>
                        handleContactSelection(contact, checked)
                      }
                    />
                  );
                }}
              />
            </div>

            <div className="btn-container">
              <GradientButton onClick={handleNextStep} className="next-btn">
                Next
              </GradientButton>
            </div>
          </div>
        </div>
      )}

      {currentScreen === "create-group" && (
        <CreateGroup
          handleBackClick={() => setCurrentScreen("add-members")}
          selectedMembers={selectedContacts}
          setSelectedMembers={setSelectedContacts}
        />
      )}

      <Modal
        centered
        title={"Confirm Unblock"}
        open={showBlockModal}
        onCancel={() => {
          setShowBlockModal(false);
          setSelectedNumberToUnblock(null);
        }}
        footer={
          <>
            <Button
              className="modal-cancel-btn"
              type="text"
              onClick={() => setShowBlockModal(false)}
            >
              Cancel
            </Button>
            <Button
              className="modal-ok-btn danger"
              onClick={handleUnblockContact}
              loading={loading}
            >
              Unblock
            </Button>
          </>
        }
        className="custom-modal"
      >
        <p className="text-primary">
          Are you sure you want to Unblock this chat?
        </p>
      </Modal>
    </>
  );
};

export default NewGroup;
